import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/solid';
import React, { FunctionComponent, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePaywallContext } from '../../contexts/paywall';
import { useSendVerificationEmail } from '../../hooks/useAccountMutations';
import * as honeybadger from '../../services/honeybadger';
import { Account } from '../../services/models/account';
import { ApplicationAlert } from '../../services/models/application-alert';
import { SubscriptionStatus } from '../../services/models/subscription';
import { classNames } from '../../utils/class-names';
import { BillingRoute } from '../../utils/Routes';

interface Props {
  account: Account;
  alerts: ApplicationAlert[];
  isVisible: boolean;
  onDismiss: () => void;
}

const NotificationHub: FunctionComponent<Props> = ({ account, alerts, isVisible, onDismiss }) => {
  const [index, setIndex] = useState(0);
  const activeAlert = alerts[index];

  const getButtons = () => {
    if (alerts.length === 1) {
      return null;
    }

    const showPrev = index >= 1;
    const showNext = index < alerts.length - 1;

    // TODO(Jose): properly enable this, at the moment is hidden "md:block"
    return (
      <nav
        className="z-0 inline-flex rounded-md shadow-xs -space-x-px hidden"
        aria-label="Pagination"
      >
        <button
          onClick={() => setIndex(index - 1)}
          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          disabled={!showPrev}
        >
          <span className="sr-only">Previous</span>
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          onClick={() => setIndex(index + 1)}
          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          disabled={!showNext}
        >
          <span className="sr-only">Next</span>
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </nav>
    );
  };

  const renderAlert = (alert: ApplicationAlert) => {
    if (alert === ApplicationAlert.UPSELL) {
      return <UpsellNotification account={account} onDismiss={onDismiss} />;
    }

    if (alert === ApplicationAlert.PAST_DUE) {
      return <PastDueNotification />;
    }

    if (alert === ApplicationAlert.EMAIL_CONFIRMATION) {
      return <EmailVerificationNotification account={account} />;
    }

    return null;
  };

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-0 inset-x-0 pb-2 sm:pb-5 md:ml-64">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div
          className={classNames(
            activeAlert === ApplicationAlert.EMAIL_CONFIRMATION
              ? 'bg-yellow-200'
              : activeAlert === ApplicationAlert.PAST_DUE
              ? 'bg-red-700'
              : 'bg-secondary',
            'p-2 rounded-lg shadow-lg sm:p-3'
          )}
        >
          <div className="flex items-center justify-between flex-wrap">
            {renderAlert(activeAlert)}

            <div className="shrink-0 mt-0 w-auto">{getButtons()}</div>

            <div className="shrink-0 sm:ml-2">
              <button
                type="button"
                className={classNames(
                  activeAlert === ApplicationAlert.EMAIL_CONFIRMATION
                    ? 'text-yellow-500 hover:bg-yellow-200'
                    : activeAlert === ApplicationAlert.PAST_DUE
                    ? 'text-white hover:bg-red-800'
                    : 'text-white hover:bg-secondary-dark',
                  '-mr-1 flex p-2 rounded-md  focus:outline-hidden focus:ring-2 focus:ring-white'
                )}
                onClick={() => onDismiss()}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationHub;

interface UpsellNotificationProps {
  account: Account;
  onDismiss: () => void;
}

function UpsellNotification(props: UpsellNotificationProps) {
  const { setPaywallModalShown } = usePaywallContext();

  const account = props.account;
  const trialExpired = account.subscription.status === SubscriptionStatus.TrialExpired;
  const trialDaysLeft = account.subscription.trialDaysLeft;

  if (
    account.subscription.status !== SubscriptionStatus.Trial &&
    account.subscription.status !== SubscriptionStatus.TrialExpired
  ) {
    // Ignore for users not in trial
    return null;
  }

  const handleClick = () => {
    props.onDismiss();
    setPaywallModalShown(true);
  };

  return (
    <div className="w-0 flex-1 flex items-center">
      <span className="flex p-2 rounded-lg bg-secondary-dark">
        <MegaphoneIcon className="h-6 w-6 text-white" aria-hidden="true" />
      </span>
      <p className="ml-3 font-medium text-white truncate">
        <span className="inline">
          {trialExpired && 'Your trial has ended'}
          {!trialExpired && trialDaysLeft > 0
            ? `${account?.subscription?.trialDaysLeft} days left in your trial`
            : 'Enjoying your trial?'}
        </span>
        <span className="block ml-2 inline-block">
          <button
            onClick={() => handleClick()}
            className="text-white font-bold underline hover:text-white"
          >
            {' '}
            Upgrade now <span aria-hidden="true">&rarr;</span>
          </button>
        </span>
      </p>
    </div>
  );
}

interface EmailVerificationNotificationProps {
  account: Account;
}

function EmailVerificationNotification(props: EmailVerificationNotificationProps) {
  const { sendVerificationEmail } = useSendVerificationEmail();

  const account = props.account;

  if (!account || !account.email) {
    honeybadger.notify(
      new Error('Empty account email object'),
      'NotificationHub/EmailVerificationNotification'
    );
    return null;
  }

  return (
    <div className="w-0 flex-1 flex items-center">
      <span className="flex p-2 rounded-lg bg-yellow-300">
        <MegaphoneIcon className="h-6 w-6 text-yellow-900" aria-hidden="true" />
      </span>
      <p className="ml-3 font-medium text-yellow-900 truncate">
        <span className="inline md:hidden">Please confirm your email address</span>
        <span className="hidden md:inline">
          Please confirm your email address: {account.email.emailAddress}.{' '}
        </span>
        <span className="block ml-2 inline-block">
          <button
            className="text-yellow-900 font-bold underline hover:text-yellow-900"
            onClick={() => sendVerificationEmail()}
          >
            Resend email <span aria-hidden="true">&rarr;</span>
          </button>
        </span>
      </p>
    </div>
  );
}

function PastDueNotification() {
  return (
    <div className="w-0 flex-1 flex items-center">
      <span className="flex p-2 rounded-lg bg-red-800">
        <ExclamationTriangleIcon className="h-6 w-6 text-white" aria-hidden="true" />
      </span>
      <p className="ml-3 font-medium text-white truncate">
        <span className="hidden md:inline">Your payment method recently failed to process.</span>
        <span className="block ml-2 inline-block">
          <Link to={BillingRoute} className="text-white font-bold underline hover:text-white">
            Update your payment information <span aria-hidden="true">&rarr;</span>
          </Link>
        </span>
      </p>
    </div>
  );
}
